import React, { useState, useEffect, useRef } from 'react';
import { TextVisitCase } from 'types/textVisit';
import s from './s.module.less';
import { Modal, Button } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import ScriptSureLogo from 'assets/common/ScriptSureLogo.png';
import useGetEligibleStatus from 'hooks/textVisit/useGetEligibleStatus';
import useGetPPAPaymentInfo from 'hooks/useGetPPAPaymentInfo';
import useProviderHomeInfo from 'hooks/useProviderHomeInfo';
import usePracticeStateList from 'hooks/usePracticeStateList';
import useGetPrescriptionByTextVisit from 'hooks/textVisit/useGetPrescriptionByTextVisit';
import { EPayProviderListType } from 'types/provider';
import ConfirmPaymentModal from 'pages/dashboard/PrescriptionsPage/PrescriptionsLandingPage/ConfirmPaymentModal';
import PrescribeOnboardingModal from '../PrescribeOnboardingModal';
import PaymentBanner from 'pages/dashboard/TextVisitPage/imgs/PaymentBanner.png';
import AddAndUpdateProspects from 'components/AddAndUpdateProspects';
import PrescriptionCard from 'components/PrescriptionCard';
import { openManageSubscriptionModal } from 'utils/globalLayerControl';
import EditPrescribeMedicationModal from 'pages/dashboard/PrescriptionsPage/PrescriptionsDashboradPage/components/EditPrescribeMedicationModal';
import AddPrescribeMedicationModal from 'pages/dashboard/PrescriptionsPage/PrescriptionsDashboradPage/components/AddPrescribeMedicationModal';
import GlobalLayerStore from 'store/GlobalLayer';
import { IVideoTreatmentDetail } from 'types/treatment';
import { canSkipPaymentConfirmForPrescription } from 'utils/common';
import useCurrentUser from 'hooks/useCurrentUser';

type Props = {
    caseData?: TextVisitCase | IVideoTreatmentDetail;
    refetch?: (businessId: string | number) => void;
};

const PrescribeMedication = ({ caseData, refetch }: Props) => {
    const [eligibleStatus, loadingStatus, fetchEligibleStatus] = useGetEligibleStatus();
    const [paymentInfo, loading, , fetchPaymentInfo] = useGetPPAPaymentInfo();
    const [homeInfo, _, refetchHomeInfo] = useProviderHomeInfo();
    const [serverPracticeStateList, serverPracticeStateListLoading, fetchServerPracticeStateList] =
        usePracticeStateList();
    const [prescriptionList, prescriptionListLoading, fetchPrescriptionList] = useGetPrescriptionByTextVisit();

    // 监听 ManageSubscriptionModal 的关闭事件
    const [getGlobalLayer] = GlobalLayerStore.useStore();
    const manageSubscriptionModalState = getGlobalLayer('showManageSubscriptionModal');
    const prevModalStateRef = useRef<boolean | undefined>(undefined);

    const [showPrescribeModal, setShowPrescribeModal] = useState(false);
    const [showConfirmPaymentModal, setShowConfirmPaymentModal] = useState(false);
    const [showScriptSureOnboardingModal, setShowScriptSureOnboardingModal] = useState(false);
    const [showUnSupportStateModal, setShowUnSupportStateModal] = useState(false);
    const [missFields, setMissFields] = useState<string[]>([]);
    const [showMissProfileFieldsModal, setShowMissProfileFieldsModal] = useState(false);
    const [showEditClientModal, setShowEditClientModal] = useState(false);
    const [showEditPrescribeMedicationModal, setShowEditPrescribeMedicationModal] = useState(false);
    const [currentUser] = useCurrentUser();
    const skipPaymentConfirmForPrescription = canSkipPaymentConfirmForPrescription(currentUser);

    const hasPay =
        homeInfo &&
        homeInfo.payProviderList &&
        homeInfo.payProviderList?.includes(EPayProviderListType.KLARITY_E_PRESCRIPTIONS);
    const hasCompletedOnboarding = eligibleStatus?.completedScriptSureOnboarding;
    const supportStates = serverPracticeStateList?.map((item) => item.state) || [];

    // 监听 ManageSubscriptionModal 关闭事件，当模态框从打开状态变为关闭状态时刷新数据
    useEffect(() => {
        const currentShow = manageSubscriptionModalState?.show;
        const prevShow = prevModalStateRef.current;
        if (prevShow === true && currentShow === false) {
            fetchServerPracticeStateList();
        }
        // 更新上一次的状态
        prevModalStateRef.current = currentShow;
    }, [manageSubscriptionModalState?.show]);

    if (!caseData) {
        return null;
    }

    const handlePrescribeClick = () => {
        if (!hasCompletedOnboarding && !skipPaymentConfirmForPrescription) {
            if (hasPay) {
                setShowScriptSureOnboardingModal(true);
            } else {
                setShowConfirmPaymentModal(true);
            }
            return;
        }
        const { prospect } = caseData;
        if (prospect?.state && !supportStates.includes(prospect?.state)) {
            setShowUnSupportStateModal(true);
            return;
        }
        const _missField: string[] = [];
        if (!prospect.address) {
            _missField.push('Address');
        }
        if (!prospect.gender) {
            _missField.push('Gender');
        }
        if (!prospect.city) {
            _missField.push('City');
        }
        if (!prospect.zip) {
            _missField.push('Zip');
        }
        if (!prospect.tel) {
            _missField.push('Phone');
        }
        if (!prospect.birthday) {
            _missField.push('Birthday');
        }
        if (!prospect.firstName) {
            _missField.push('First Name');
        }
        if (!prospect.lastName) {
            _missField.push('Last Name');
        }
        if (!prospect.state) {
            _missField.push('State');
        }
        if (_missField.length > 0) {
            setMissFields(_missField);
            setShowMissProfileFieldsModal(true);
            return;
        } else {
            setMissFields([]);
        }
        if (hasCompletedOnboarding) {
            setShowPrescribeModal(true);
        }
    };

    const fetchPrescriptionListLater = () => {
        try {
            setTimeout(() => {
                fetchPrescriptionList(caseData.id);
            }, 3000);
        } catch (e) {
            console.error(e);
        }
    };

    const displayPrescriptionList = caseData.prescriptions || prescriptionList || [];

    return (
        <>
            <div className="flex items-center justify-between">
                <h3 className={s.title}>Prescriptions</h3>
                {displayPrescriptionList.length > 0 && (
                    <Button onClick={() => setShowEditPrescribeMedicationModal(true)}>Edit</Button>
                )}
            </div>
            {displayPrescriptionList.length > 0 &&
                displayPrescriptionList.map((prescription, index) => (
                    <PrescriptionCard key={index} prescription={prescription} />
                ))}
            <div className={s.prescribeCard} onClick={handlePrescribeClick}>
                <div className="flex items-center gap-3">
                    <PlusOutlined className={s.iconBg} />
                    <span className={s.prescribeText}>Prescribe medication</span>
                </div>
                <img src={ScriptSureLogo} alt="ScriptSureLogo" className="h-5" />
            </div>
            <div className={s.sectionSubtitle}>
                👉 You can continue using your own eRX system to prescribe medication. Document medications and
                instructions in the Visit Notes section for patient records.
            </div>
            <ConfirmPaymentModal
                open={showConfirmPaymentModal}
                onClose={() => setShowConfirmPaymentModal(false)}
                onSuccess={() => {
                    setShowConfirmPaymentModal(false);
                    setShowScriptSureOnboardingModal(true);
                    refetchHomeInfo();
                }}
                paymentInfo={paymentInfo}
                onPaymentInfoChange={fetchPaymentInfo}
                title="Prescribe medications with Klarity E-Prescriptions (Powered by ScriptSure)"
                header={
                    <div>
                        <img src={PaymentBanner} alt="Payment Banner" className={s.paymentBanner} />
                        <div className="text-base text-gray-900 my-4">
                            Prescribe medications directly within Klarity using ScriptSure—fast, secure, compliant, and
                            fully integrated into your workflow, all for just{' '}
                            <span className="font-bold">$36 per month.</span>
                        </div>
                    </div>
                }
            />
            <PrescribeOnboardingModal
                open={showScriptSureOnboardingModal}
                onClose={() => setShowScriptSureOnboardingModal(false)}
                onFinish={() => {
                    setShowScriptSureOnboardingModal(false);
                    fetchEligibleStatus();
                }}
            />
            <AddPrescribeMedicationModal
                open={showPrescribeModal}
                onClose={() => {
                    setShowPrescribeModal(false);
                    fetchPrescriptionListLater();
                }}
                prospectId={showPrescribeModal ? caseData?.clientId : undefined}
            />
            <Modal
                open={showUnSupportStateModal}
                onCancel={() => setShowUnSupportStateModal(false)}
                footer={
                    <div className={s.btnWrap}>
                        <Button
                            onClick={() => {
                                setShowUnSupportStateModal(false);
                            }}
                            style={{ marginLeft: '12px' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                openManageSubscriptionModal('7');
                                setShowUnSupportStateModal(false);
                            }}
                            type="primary"
                            className={s.update}
                        >
                            Update practice information
                        </Button>
                    </div>
                }
                width={620}
            >
                <div className={s.unSupportStateModal}>
                    <div className={s.icon}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path
                                d="M11.9995 9V11M11.9995 15H12.0095M5.07134 19H18.9277C20.4673 19 21.4296 17.3333 20.6598 16L13.7316 4C12.9618 2.66667 11.0373 2.66667 10.2675 4L3.33929 16C2.56949 17.3333 3.53174 19 5.07134 19Z"
                                stroke="#DC2626"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                    <div className={s.info}>
                        <div className={s.title}>Can’t proceed with prescription</div>
                        <div className={s.des}>
                            This patient is not associated with any of your practices in their state. Please select
                            another patient or update your practice information to continue.
                        </div>
                    </div>
                </div>
            </Modal>
            <Modal
                open={showMissProfileFieldsModal}
                onCancel={() => setShowMissProfileFieldsModal(false)}
                footer={
                    <div className={s.btnWrap}>
                        <Button
                            onClick={() => {
                                setShowMissProfileFieldsModal(false);
                            }}
                            style={{ marginLeft: '12px' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                setShowEditClientModal(true);
                                setShowMissProfileFieldsModal(false);
                            }}
                            type="primary"
                            className={s.update}
                        >
                            Edit profile
                        </Button>
                    </div>
                }
                width={620}
            >
                <div className={s.unSupportStateModal}>
                    <div className={s.icon}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path
                                d="M11.9995 9V11M11.9995 15H12.0095M5.07134 19H18.9277C20.4673 19 21.4296 17.3333 20.6598 16L13.7316 4C12.9618 2.66667 11.0373 2.66667 10.2675 4L3.33929 16C2.56949 17.3333 3.53174 19 5.07134 19Z"
                                stroke="#DC2626"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                    <div className={s.info}>
                        <div className={s.title}>Can’t proceed with prescription</div>
                        <div className={s.des}>
                            <p>This patient is missing the following information:</p>
                            <ul>
                                {missFields.map((item) => (
                                    <li key={item}>{item}</li>
                                ))}
                            </ul>
                            <p>To continue with prescribing, please add the information to their profile. </p>
                        </div>
                    </div>
                </div>
            </Modal>
            {showEditClientModal && (
                <AddAndUpdateProspects
                    show={showEditClientModal}
                    onCannel={() => {
                        setShowEditClientModal(false);
                    }}
                    onSuccess={() => {
                        setShowEditClientModal(false);
                        refetch?.((caseData as TextVisitCase).textVisitUuid || (caseData as IVideoTreatmentDetail).id);
                    }}
                    item={caseData.prospect}
                />
            )}
            <EditPrescribeMedicationModal
                open={showEditPrescribeMedicationModal}
                onClose={() => {
                    setShowEditPrescribeMedicationModal(false);
                    fetchPrescriptionListLater();
                }}
                prospectId={showEditPrescribeMedicationModal ? caseData?.clientId : undefined}
            />
        </>
    );
};

export default PrescribeMedication;
